import { Box, Button, Dialog, Icon, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { useFormatMessage } from "lang";
import { isFirefox, isIOS, isIOSSafari, isPWA } from "utils/detectDevice";
import { useTypewriter } from "utils/hooks/useTypewriter";
import { getFromLocalStorage, setToLocalStorage } from "utils/localStorage";

import { BeforeInstallPromptEvent } from "./Sidebar/components/types";

const DISMISSED_KEY = "pwa-install-dismissed";
const TYPEWRITER_SPEED = 80;
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const isDismissed = (): boolean => {
  const dismissedAt = getFromLocalStorage<number>(DISMISSED_KEY);
  if (!dismissedAt) return false;
  return Date.now() - dismissedAt < DISMISS_DURATION_MS;
};

const fadeInSx = (delay: number) => ({
  opacity: 0,
  transform: "translateY(20px)",
  "@keyframes pwaFadeIn": {
    to: { opacity: 1, transform: "translateY(0)" },
  },
  animation: `pwaFadeIn 1.2s ease-out ${delay}s forwards`,
});

export const PWAInstallDialog = () => {
  const formatMessage = useFormatMessage();
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  const greetingText = formatMessage("pwa.modalTitle.greeting");
  const messageText = formatMessage("pwa.modalTitle.message");
  const greeting = useTypewriter(open ? greetingText : "", {
    speed: TYPEWRITER_SPEED,
  });
  const messageDelay = greetingText.length * TYPEWRITER_SPEED + 300;
  const message = useTypewriter(open ? messageText : "", {
    speed: TYPEWRITER_SPEED,
    delay: messageDelay,
  });
  const typewriterDoneMs =
    messageDelay + messageText.length * TYPEWRITER_SPEED + 500;
  const typewriterDoneS = typewriterDoneMs / 1000;

  const iosSafari = isIOSSafari() && !isPWA();
  const iosNotSafari = isIOS() && !isIOSSafari() && !isPWA();
  const firefox = isFirefox() && !isPWA();

  useEffect(() => {
    if (isPWA() || isDismissed()) return;

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setOpen(true);
    };

    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt as EventListener,
    );

    // For iOS/Firefox, show immediately since beforeinstallprompt won't fire
    if (isIOS() || isFirefox()) {
      setOpen(true);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt as EventListener,
      );
    };
  }, []);

  const handleRemindLater = () => {
    setOpen(false);
  };

  const handleDismiss = () => {
    setOpen(false);
    setToLocalStorage(DISMISSED_KEY, Date.now());
  };

  const handleInstall = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(() => {
        setDeferredPrompt(null);
        setOpen(false);
        setToLocalStorage(DISMISSED_KEY, Date.now());
      });
    }
  };

  const getInfoContent = () => {
    if (iosNotSafari) {
      return formatMessage("pwa.iosNotSafari");
    }
    if (iosSafari) {
      return (
        <>
          {formatMessage("pwa.iosInfo", {
            values: { shareIcon: "\u200B" },
          })
            .split("\u200B")
            .map((part, i, arr) => (
              <span key={i}>
                {part}
                {i < arr.length - 1 && (
                  <Icon sx={{ fontSize: 16, verticalAlign: "middle" }}>
                    ios_share
                  </Icon>
                )}
              </span>
            ))}
        </>
      );
    }
    if (firefox) {
      return formatMessage("pwa.firefoxInfo");
    }
    return formatMessage("pwa.info");
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 1,
          maxWidth: 360,
        },
      }}
    >
      <Stack sx={{ px: 6, py: 4, alignItems: "center", textAlign: "center" }}>
        <Typography
          sx={{
            my: 4,
            fontWeight: 700,
            fontSize: 32,
            alignSelf: "flex-start",
            textAlign: "left",
            lineHeight: 1.2,
          }}
        >
          {greeting.typedText} {message.typedText}
        </Typography>

        <Box
          component="img"
          src="/icons/pwa-maskable-512x512.png"
          alt="Solar logo"
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            ...fadeInSx(typewriterDoneS),
          }}
        />
        <Typography
          color="primary"
          sx={{
            fontSize: 24,
            fontWeight: 900,
            lineHeight: 1.2,
            mt: 1,
            ...fadeInSx(typewriterDoneS),
          }}
        >
          Solar
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 6, ...fadeInSx(typewriterDoneS + 1) }}
        >
          {getInfoContent()}
        </Typography>

        <Stack
          sx={{
            gap: 1,
            mt: 4,
            width: "100%",
            ...fadeInSx(typewriterDoneS + 1.5),
          }}
        >
          {deferredPrompt && (
            <Button
              onClick={handleInstall}
              variant="contained"
              fullWidth
              sx={{ textTransform: "none" }}
            >
              {formatMessage("pwa.getTheApp")}
            </Button>
          )}
          <Stack direction="row" justifyContent="center" sx={{ gap: 1 }}>
            <Button onClick={handleDismiss} sx={{ textTransform: "none" }}>
              {formatMessage("pwa.modalDismiss")}
            </Button>
            <Button onClick={handleRemindLater} sx={{ textTransform: "none" }}>
              {formatMessage("pwa.modalRemindLater")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  );
};
